import { parseNumberInput } from "@/lib/parse-number";
import {
  divR,
  intR,
  mulR,
  parseRational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Read a typed angle in degrees or radians.
 *
 * In radians a multiple of π can be typed as `pi/6`, `2π/3` or `-pi`, which is exact; a
 * plain number such as 0.5236 is a decimal and is never mistaken for an exact angle.
 */

export type AngleUnit = "degrees" | "radians";

export interface ParsedAngle {
  degrees: number;
  /** The angle in whole degrees when the input is exactly that; null otherwise. */
  exactDegrees: number | null;
}

const PI_FORM =
  /^([+\-−])?\s*(\d+(?:[.,]\d+)?)?\s*\*?\s*(?:π|pi)\s*(?:\/\s*(\d+))?$/i;

function fromRationalDegrees(num: bigint, den: bigint): ParsedAngle {
  const degrees = Number(num) / Number(den);
  return {
    degrees,
    exactDegrees: den === BigInt(1) ? Number(num) : null,
  };
}

export function parseAngle(text: string, unit: AngleUnit): ParsedAngle | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  if (unit === "degrees") {
    const exact = parseRational(trimmed);
    if (exact === null) {
      return null;
    }
    return {
      degrees: toNumberR(exact),
      exactDegrees: exact.den === BigInt(1) ? Number(exact.num) : null,
    };
  }
  const match = PI_FORM.exec(trimmed);
  if (match) {
    const coefficient = parseRational(match[2] ?? "1");
    const divisor = match[3] ? parseRational(match[3]) : intR(1);
    if (coefficient === null || divisor === null || divisor.num === BigInt(0)) {
      return null;
    }
    let multiple = divR(coefficient, divisor);
    if (match[1] === "-" || match[1] === "−") {
      multiple = mulR(multiple, intR(-1));
    }
    const degrees = mulR(multiple, intR(180));
    return fromRationalDegrees(degrees.num, degrees.den);
  }
  const radians = parseNumberInput(trimmed);
  return radians === null
    ? null
    : { degrees: (radians * 180) / Math.PI, exactDegrees: null };
}
